/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * The words shown for each access notice.
 */

import { ACCESS_NOTICE_KINDS } from "@lemonadetraining/contracts";
import type {
  AccessNotice,
  AccessNoticeKind,
  AccessNoticeProgramme,
} from "@lemonadetraining/contracts";
import type { ProgrammeOrigin } from "../../Common/privateProgramme";

/*
 * Derived from the published contract rather than restated, but deliberately
 * loose in two ways: `kind` stays a string so a notice minted by a later
 * release reaches presentNotice and is ignored rather than trusted, and every
 * other field is optional because nothing validates the document client-side.
 */
export type NoticeProgramme = Partial<AccessNoticeProgramme>;

export type StoredNotice = Partial<Omit<AccessNotice, "kind" | "programmes">> & {
  id: string;
  kind: string;
  programmes?: NoticeProgramme[];
};

/** Where each kind is shown. Exhaustive, so a new kind is a compile error. */
export type NoticeSurface = "banner" | "modal" | "page" | "toast";

export const SURFACE_BY_KIND: Record<AccessNoticeKind, NoticeSurface> = {
  promoted_admin: "banner",
  // Also replaces the page when the person is standing on an admin-only route.
  demoted_admin: "banner",
  creation_grant_changed: "toast",
  removed_from_staff: "modal",
  programme_handoff: "banner",
  programme_received: "banner",
};

export const surfaceOf = (kind: string): NoticeSurface | undefined =>
  SURFACE_BY_KIND[kind as AccessNoticeKind];

export interface NoticeContext {
  /** Drives the "you're still a coach" line rather than the membership one. */
  isBoxCoach: boolean;
}

export interface PresentedNotice {
  title: string;
  body: string[];
  action?: { label: string; to: string; state?: { programmeOrigin: ProgrammeOrigin } };
}

/**
 * Attribution is by role, never by name (LD-3): the stored notice carries no
 * name field, and the actor's identity lives in the gym's RoleAudit ledger
 * where only owners and admins can see it.
 */
export const attribution = (changedByRole?: string): string =>
  changedByRole === "owner" ? "the gym's owner" : "a gym admin";

const gymOf = (notice: StoredNotice): string => notice.gymName || "your gym";

const isKnownKind = (kind: string): kind is AccessNoticeKind =>
  (ACCESS_NOTICE_KINDS as readonly string[]).includes(kind);

const handoffLine = (programme: NoticeProgramme, gymName: string): string => {
  const name = programme.name || "Your programme";
  if (programme.outcome === "reassigned") {
    const coach = programme.targetCoachDisplayName;
    return coach
      ? `${name} is now looked after by ${coach}. Everything in it is unchanged.`
      : `${name} is now looked after by another coach. Everything in it is unchanged.`;
  }
  if (programme.outcome === "kept_by_gym") {
    return `${name} is now looked after by ${gymName}. Everything in it is unchanged.`;
  }
  return `${name} has ended. You can still see all your workouts and results.`;
};

/** What the removed coach is told happened to each programme they owned. */
export const removedOutcomeLine = (programme: NoticeProgramme): string => {
  const name = programme.name || "Programme";
  if (programme.outcome === "reassigned") return `${name}: handed to another coach`;
  if (programme.outcome === "kept_by_gym") return `${name}: kept by the gym`;
  return `${name}: ended`;
};

/**
 * Returns null for a kind this build does not know, so a notice minted by a
 * later release renders nothing rather than a blank or a guess.
 */
export const presentNotice = (
  notice: StoredNotice,
  context: NoticeContext
): PresentedNotice | null => {
  if (!isKnownKind(notice.kind)) return null;
  const gym = gymOf(notice);
  const by = attribution(notice.changedByRole);

  switch (notice.kind) {
    case "promoted_admin":
      return {
        title: `You're now an admin at ${gym}.`,
        body: [
          "You can now manage the gym: your team, memberships and gym settings, all from the sidebar.",
        ],
        action: { label: "Open Team", to: "/dashboard/team" },
      };

    case "demoted_admin":
      return {
        title: `Your admin access at ${gym} was removed by ${by}.`,
        body: [
          context.isBoxCoach
            ? "You're still a coach here. Your programmes and classes are unchanged."
            : "Your membership and bookings continue as before.",
        ],
      };

    case "creation_grant_changed":
      return {
        title: `Your programme-creation access was changed by ${by}.`,
        body: [],
      };

    case "removed_from_staff": {
      const programmes = notice.programmes ?? [];
      return {
        title: `You're no longer on the staff at ${gym}.`,
        body: [
          `Changed by ${by}.`,
          ...(programmes.length > 0 ? ["Your PT programmes were handed over."] : []),
          "Your own workouts and results are still yours.",
        ],
      };
    }

    case "programme_handoff": {
      const programme = notice.programmes?.[0] ?? {};
      return { title: "A programme of yours changed hands.", body: [handoffLine(programme, gym)] };
    }

    case "programme_received": {
      const programme = notice.programmes?.[0] ?? {};
      const athlete = programme.athleteFirstName;
      const name = programme.name || "a programme";
      return {
        title: athlete
          ? `You now look after ${athlete}'s programme ${name}.`
          : `You now look after the programme ${name}.`,
        body: [],
        action: programme.programmeUID
          ? {
              label: "Open programme",
              to: `/dashboard/workout-programme/${programme.programmeUID}`,
              state: { programmeOrigin: "own-lane" },
            }
          : undefined,
      };
    }

    default:
      return null;
  }
};
